import React, { useEffect, useState } from "react";
import { ModelInputs, dateKeys, boolKeys } from "../domain/types";
import { formatLabel } from "../domain/utils";

// BA codes extracted at build time from GeoJSON — short code + full zone for display
const BA_OPTIONS: { value: string; label: string }[] = [
  { value: 'AECI', label: 'AECI — Associated Electric (Midwest)' },
  { value: 'AVA', label: 'AVA — Avista (Northwest)' },
  { value: 'BANC', label: 'BANC — Balancing Authority of Northern California' },
  { value: 'BPAT', label: 'BPAT — Bonneville Power Administration' },
  { value: 'CHPD', label: 'CHPD — Chelan County PUD (Northwest)' },
  { value: 'CISO', label: 'CISO — California ISO' },
  { value: 'CPLE', label: 'CPLE — Duke Energy Progress East (Carolinas)' },
  { value: 'CPLW', label: 'CPLW — Duke Energy Progress West (Carolinas)' },
  { value: 'DOPD', label: 'DOPD — Douglas County PUD (Northwest)' },
  { value: 'DUK', label: 'DUK — Duke Energy Carolinas' },
  { value: 'EPE', label: 'EPE — El Paso Electric (Southwest)' },
  { value: 'ERCO', label: 'ERCO — ERCOT (Texas)' },
  { value: 'FMPP', label: 'FMPP — Florida Municipal Power Pool' },
  { value: 'FPC', label: 'FPC — Duke Energy Florida' },
  { value: 'FPL', label: 'FPL — Florida Power & Light' },
  { value: 'GCPD', label: 'GCPD — Grant County PUD (Northwest)' },
  { value: 'GVL', label: 'GVL — Gainesville Regional Utilities (Florida)' },
  { value: 'HST', label: 'HST — Homestead (Florida)' },
  { value: 'IID', label: 'IID — Imperial Irrigation District (California)' },
  { value: 'IPCO', label: 'IPCO — Idaho Power (Northwest)' },
  { value: 'ISNE', label: 'ISNE — ISO New England' },
  { value: 'JEA', label: 'JEA — Jacksonville Electric Authority (Florida)' },
  { value: 'LDWP', label: 'LDWP — Los Angeles DWP (California)' },
  { value: 'LGEE', label: 'LGEE — Louisville Gas & Electric (Midwest)' },
  { value: 'MISO', label: 'MISO — Midcontinent ISO' },
  { value: 'NEVP', label: 'NEVP — Nevada Power (Northwest)' },
  { value: 'NWMT', label: 'NWMT — NorthWestern Energy Montana' },
  { value: 'NYIS', label: 'NYIS — New York ISO' },
  { value: 'PACE', label: 'PACE — PacifiCorp East (Northwest)' },
  { value: 'PACW', label: 'PACW — PacifiCorp West (Northwest)' },
  { value: 'PGE', label: 'PGE — Portland General Electric (Northwest)' },
  { value: 'PJM', label: 'PJM — PJM Interconnection (Mid-Atlantic)' },
  { value: 'PNM', label: 'PNM — Public Service Co of New Mexico (Southwest)' },
  { value: 'PSCO', label: 'PSCO — Public Service Co of Colorado (Northwest)' },
  { value: 'PSEI', label: 'PSEI — Puget Sound Energy (Northwest)' },
  { value: 'SC', label: 'SC — South Carolina Public Service Authority' },
  { value: 'SCEG', label: 'SCEG — Dominion Energy South Carolina' },
  { value: 'SCL', label: 'SCL — Seattle City Light (Northwest)' },
  { value: 'SEC', label: 'SEC — Seminole Electric (Florida)' },
  { value: 'SOCO', label: 'SOCO — Southern Company (Southeast)' },
  { value: 'SPA', label: 'SPA — Southwestern Power Administration (Central)' },
  { value: 'SRP', label: 'SRP — Salt River Project (Southwest)' },
  { value: 'SWPP', label: 'SWPP — Southwest Power Pool' },
  { value: 'TAL', label: 'TAL — City of Tallahassee (Florida)' },
  { value: 'TEC', label: 'TEC — Tampa Electric (Florida)' },
  { value: 'TEPC', label: 'TEPC — Tucson Electric Power (Southwest)' },
  { value: 'TIDC', label: 'TIDC — Turlock Irrigation District (California)' },
  { value: 'TPWR', label: 'TPWR — City of Tacoma (Northwest)' },
  { value: 'TVA', label: 'TVA — Tennessee Valley Authority' },
  { value: 'WACM', label: 'WACM — Western Area Power (CO/MT/NE/WY)' },
  { value: 'WALC', label: 'WALC — Western Area Power (Southwest)' },
  { value: 'WAUW', label: 'WAUW — Western Area Power (Upper Great Plains)' },
  { value: 'AZPS', label: 'AZPS — Arizona Public Service (Southwest)' },
];

// Tech type options - user-friendly values that map to ecoinvent activities in the backend
const TECH_TYPE_OPTIONS = [
  { value: 'solar_ground', label: 'Solar - Ground Mount' },
  { value: 'solar_rooftop', label: 'Solar - Rooftop' },
  { value: 'wind_onshore', label: 'Wind - Onshore' },
  { value: 'wind_offshore', label: 'Wind - Offshore' },
];

// Battery chemistry options for BESS LCA
const BATTERY_CHEMISTRY_OPTIONS = [
  { value: 'lfp', label: 'LFP (Lithium Iron Phosphate)' },
  { value: 'nmc811', label: 'NMC 811' },
  { value: 'nmc622', label: 'NMC 622' },
];

type Props = {
  name: keyof ModelInputs;
  value: ModelInputs[keyof ModelInputs];
  onChange: (name: keyof ModelInputs, value: any) => void;
  required?: boolean;
};

export const Field: React.FC<Props> = ({ name, value, onChange, required }) => {
  if (name === "customer_id" || name === "gen_hour_profile_mw") return null;

  // Handle boolean fields
  if ((boolKeys as readonly (keyof ModelInputs)[]).includes(name)) {
    return (
      <div className="flex flex-col">
        <label className="mb-1 text-body-sm font-medium text-text-secondary flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="mr-2 accent-quantum-500 w-4 h-4"
            checked={!!value}
            onChange={(e) => onChange(name, e.currentTarget.checked)}
          />
          {formatLabel(String(name))}
        </label>
      </div>
    );
  }

  // Handle eia_ba_code as searchable dropdown
  if (name === "eia_ba_code") {
    return (
      <div className="flex flex-col">
        <label htmlFor={String(name)} className="input-label">
          Balancing Authority
        </label>
        <input
          id={String(name)}
          name={String(name)}
          type="text"
          list="ba-options"
          value={value as string}
          onChange={(e) => onChange(name, e.target.value)}
          className="input"
          placeholder="Type to search or select..."
          autoComplete="off"
        />
        <datalist id="ba-options">
          {BA_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </datalist>
      </div>
    );
  }

  // Handle tech_type_id as dropdown
  if (name === "tech_type_id") {
    return (
      <div className="flex flex-col">
        <label htmlFor={String(name)} className="input-label">
          {formatLabel(String(name))}
          {required && <span className="text-health-400 ml-1">*</span>}
        </label>
        <select
          id={String(name)}
          name={String(name)}
          value={value as string}
          onChange={(e) => onChange(name, e.target.value)}
          className="input"
          required={required}
        >
          <option value="">Select a technology type...</option>
          {TECH_TYPE_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Handle battery_chemistry as dropdown
  if (name === "battery_chemistry") {
    return (
      <div className="flex flex-col">
        <label htmlFor={String(name)} className="input-label">
          {formatLabel(String(name))}
        </label>
        <select
          id={String(name)}
          name={String(name)}
          value={value as string}
          onChange={(e) => onChange(name, e.target.value)}
          className="input"
        >
          {BATTERY_CHEMISTRY_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  const isDate = (dateKeys as readonly (keyof ModelInputs)[]).includes(name);

  // Default case: render text/date input
  return (
    <div className="flex flex-col">
      <label htmlFor={String(name)} className="input-label">
        {formatLabel(String(name))}
        {required && <span className="text-health-400 ml-1">*</span>}
      </label>
      <input
        id={String(name)}
        name={String(name)}
        type={isDate ? "date" : "text"}
        value={value as any}
        required={required}
        onChange={(e) => onChange(name, (e.target as HTMLInputElement).value)}
        className="input"
      />
    </div>
  );
};
